const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

class TreatmentPlanService {
  static async createTreatmentPlan(treatmentPlanData, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/treatment-plans`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(treatmentPlanData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error creating treatment plan');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating treatment plan:', error);
      throw error;
    }
  }

  static async getTreatmentPlans(token, filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      Object.keys(filters).forEach(key => {
        if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
          queryParams.append(key, filters[key]);
        }
      });

      const url = `${API_BASE_URL}/treatment-plans${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error fetching treatment plans');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching treatment plans:', error);
      throw error;
    }
  }

  static async getTreatmentPlanById(id, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/treatment-plans/${id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error fetching treatment plan');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching treatment plan:', error);
      throw error;
    }
  }

  static async getTreatmentPlansByPatient(patientId, token, options = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (options.includeArchived) {
        queryParams.append('includeArchived', 'true');
      }

      const url = `${API_BASE_URL}/treatment-plans/patient/${patientId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error fetching patient treatment plans');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching patient treatment plans:', error);
      throw error;
    }
  }

  static async updateTreatmentPlan(id, updateData, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/treatment-plans/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error updating treatment plan');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating treatment plan:', error);
      throw error;
    }
  }

  static async deleteTreatmentPlan(id, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/treatment-plans/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error deleting treatment plan');
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting treatment plan:', error);
      throw error;
    }
  }

  static async updateTreatmentPlanStatus(id, status, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/treatment-plans/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error updating treatment plan status');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating treatment plan status:', error);
      throw error;
    }
  }

  static async getTreatmentPlanStats(token, filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      Object.keys(filters).forEach(key => {
        if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
          queryParams.append(key, filters[key]);
        }
      });

      const url = `${API_BASE_URL}/treatment-plans/stats/summary${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error fetching treatment plan stats');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching treatment plan stats:', error);
      throw error;
    }
  }

  static async issueTreatmentPlanInvoice(id, invoiceData, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/treatment-plans/${id}/invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(invoiceData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        const detail = errorData.detail ? ` (${errorData.detail})` : '';
        throw new Error((errorData.message || 'Error generating invoice') + detail);
      }

      return await response.json();
    } catch (error) {
      console.error('Error generating invoice:', error);
      throw error;
    }
  }

  static async downloadTreatmentPlanInvoiceXml(id, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/treatment-plans/${id}/invoice/xml`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error downloading XML');
      }

      return await response.blob();
    } catch (error) {
      console.error('Error downloading invoice XML:', error);
      throw error;
    }
  }

  static async downloadTreatmentPlanInvoicePdf(id, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/treatment-plans/${id}/invoice/pdf`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error downloading PDF');
      }

      return await response.blob();
    } catch (error) {
      console.error('Error downloading invoice PDF:', error);
      throw error;
    }
  }

  static async sendTreatmentPlanEmail(id, payload, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/treatment-plans/${id}/email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error sending treatment plan email');
      }

      return await response.json();
    } catch (error) {
      console.error('Error sending treatment plan email:', error);
      throw error;
    }
  }
}

export default TreatmentPlanService;
